import { DEFAULT_VESSEL_BEAM, DEFAULT_VESSEL_LOA } from 'data/static';
import { OffsetType, PositionType, VesselCategoryType, VesselType } from 'types';
import { DateTimeFormat } from './dateTimeFormat';

export function vesselOutline(
    sourcPosition: PositionType,
    vessel: VesselType,
    vesselOffset: OffsetType | undefined,
    vesselCategory: VesselCategoryType,
    vesselCategories: VesselCategoryType[],
    portId: number,
    center = true
) {
    const LOA = vessel.loa_m && vessel.loa_m ? vessel.loa_m : DEFAULT_VESSEL_LOA;
    const BEAM = vessel.beam_m ? vessel.beam_m : DEFAULT_VESSEL_BEAM;

    if (sourcPosition.heading_deg == 'N/A' || sourcPosition.heading_deg == null || !vesselOffset) {
        return {
            status: true,
            color: vesselCategory.color,
            outline: createUnknownHeadingVessel(sourcPosition,  LOA / 2  ),
        };
    }

    let vesselShape = [
        [-0.5, 0],
        [-0.5, 0.2258],
        [-0.4243, 0.3859],
        [-0.3673, 0.4668],
        [-0.3157, 0.5],
        [0.3291, 0.5],
        [0.3626, 0.4734],
        [0.3991, 0.4133],
        [0.4587, 0.2508],
        [0.4909, 0.1002],
        [0.5, 0],
        [0.4909, -0.1002],
        [0.4587, -0.2508],
        [0.3991, -0.4133],
        [0.3626, -0.4734],
        [0.3291, -0.5],
        [-0.3157, -0.5],
        [-0.3673, -0.4668],
        [-0.4243, -0.3859],
        [-0.5, -0.2258],
        [-0.5, 0],
    ];

    const customVesselCategory: VesselCategoryType | undefined = vesselCategories.find((x) => {
        return x.port_id === portId && x.name === vesselCategory.name;
    });
    if (customVesselCategory !== undefined) {
        vesselShape = customVesselCategory.vessel_shape;
    } else if (Array.isArray(vesselCategory.vessel_shape)) {
        vesselShape = vesselCategory.vessel_shape;
    }

    const vesselSourceCenter = center
        ? sourcPosition
        : {
              east_utm: sourcPosition.east_utm + LOA / 2 - vesselOffset.stern_m,
              north_utm: sourcPosition.north_utm + BEAM / 2 - vesselOffset.stbd_m,
              heading_deg: sourcPosition.heading_deg,
          };

    const coordinates: number[][] = [];
    vesselShape.forEach((point) => {
        const x = point[0] * LOA + vesselSourceCenter.east_utm;
        const y = point[1] * BEAM + vesselSourceCenter.north_utm;
        coordinates.push([x, y]);
    });

    return {
        status: false,
        color: customVesselCategory ? customVesselCategory.color : vesselCategory.color,
        //@ts-ignore
        outline: rotateUTMPolygonToHeading(coordinates, vesselSourceCenter.heading_deg - 90, [
            sourcPosition.east_utm,
            sourcPosition.north_utm,
        ]),
    };
}

function createUnknownHeadingVessel(sourcPosition: PositionType, radius = 20, numPoints = 64) {
    const coordinates: number[][] = [];
    const angleStep = (2 * Math.PI) / numPoints;

    for (let i = 0; i < numPoints; i++) {
        const angle = i * angleStep;
        const x = sourcPosition.east_utm + radius * Math.cos(angle);
        const y = sourcPosition.north_utm + radius * Math.sin(angle);
        coordinates.push([x, y]);
    }

    coordinates.push(coordinates[0]);

    return coordinates;
}
function rotateUTMPolygonToHeading(coordinates, compassHeading, pivot) {
    // Adjust the angle to account for the compass-based heading
    const normalizedAngle = (360 - compassHeading) % 360; // Compass heading to mathematical rotation angle
    const angleRad = (Math.PI / 180) * normalizedAngle; // Convert angle to radians
    const cosAngle = Math.cos(angleRad);
    const sinAngle = Math.sin(angleRad);

    // Rotate each point in the polygon
    const rotatedCoordinates = coordinates.map(([x, y]) => {
        // Translate point to the origin (pivot)
        const translatedX = x - pivot[0];
        const translatedY = y - pivot[1];

        // Apply rotation matrix
        const rotatedX = translatedX * cosAngle - translatedY * sinAngle;
        const rotatedY = translatedX * sinAngle + translatedY * cosAngle;

        // Translate back to the original position
        return [rotatedX + pivot[0], rotatedY + pivot[1]];
    });

    return rotatedCoordinates;
}

export function findNearestPoint(
    points,
    target: { north_utm: number; east_utm: number },
    afterTime?: DateTimeFormat | null,
    beforeTime?: DateTimeFormat | null
): PositionType | null {
    let nearestPoint = null;
    let smallestDistance = Infinity;

    for (const point of points) {
        // Time filtering logic
        if (afterTime || beforeTime) {
            const pointTime = point.valid_time_utc.toTimestamp();
            const isAfterValid = afterTime ? pointTime >= afterTime.toTimestamp() : true;
            const isBeforeValid = beforeTime ? pointTime <= beforeTime.toTimestamp() : true;

            if (!isAfterValid || !isBeforeValid) {
                continue;
            }
        }
        // Calculate Euclidean distance
        const distance = Math.sqrt(
            Math.pow(point.north_utm - target.north_utm, 2) + Math.pow(point.east_utm - target.east_utm, 2)
        );

        if (distance < smallestDistance) {
            smallestDistance = distance;
            nearestPoint = point;
        }
    }

    return nearestPoint;
}

export function findNearestPointInTime(
    points: PositionType[],
    afterTime?: DateTimeFormat | null,
    beforeTime?: DateTimeFormat | null
): PositionType | null {
    if (!points || points.length === 0) return null;

    let nearestPoint: PositionType | null = null;

    for (const point of points) {
        const pointTime = point.valid_time_utc?.date;

        const isAfterValid = afterTime ? afterTime.isBefore(pointTime) || afterTime.isSame(pointTime) : true;
        const isBeforeValid = beforeTime ? beforeTime.isAfter(pointTime) || beforeTime.isSame(pointTime) : true;
        if (isAfterValid && isBeforeValid) {
            if (!nearestPoint || nearestPoint.valid_time_utc.isBefore(pointTime)) {
                nearestPoint = point;
            }
        }
    }

    return nearestPoint;
}
